import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ReferenceListTypes, SearchQuery } from 'utils/gloo-types';
import { getUpdatedConvoPromptsFilters } from 'utils/conversationPrompts';
import { SelectOption } from 'utils/frontendTypes';
import { useThunkDispatch } from 'utils/reduxHook';
import { getAllConversationPromptAuthors, getReferenceListValuesByType } from 'actions/requests';
import CheckboxSelect from 'components/molecules/CheckboxSelect';
import Input from 'components/molecules/Input';
import { useSelector } from 'react-redux';
import { RootState } from 'reducers';
import useDebouncedCallback from 'utils/hooks';
import { convertToSelectOptions } from 'utils';
import Checkbox from '../../atoms/Checkbox';

interface ConversationPromptsFiltersProps {
  searchQuery: SearchQuery;
  setSearchQuery: Dispatch<SetStateAction<SearchQuery>>;
  showArchived: boolean;
  setShowArchived: Dispatch<SetStateAction<boolean>>;
}

const ConversationPromptsFilters = ({
  searchQuery,
  setSearchQuery,
  showArchived,
  setShowArchived,
}: ConversationPromptsFiltersProps) => {
  const dispatch = useThunkDispatch();
  const allOption = { label: 'All', value: 'all' };
  const [openFilterId, setOpenFilterId] = useState<number>(null);

  const allTopics = useSelector(({ data }: RootState) => data.topics.data);
  const [authors, setAuthors] = useState(null);
  const [searchString, setSearchString] = useState(null);

  const getAuthorsAndTopics = async () => {
    await dispatch(getReferenceListValuesByType(ReferenceListTypes.TOPICS, 'true'));
    setAuthors(await dispatch(getAllConversationPromptAuthors()));
  };

  const statusOptions = [
    { label: 'Published', value: 'Published' },
    { label: 'Unpublished', value: 'Unpublished' },
    { label: 'Draft', value: 'Draft' },
  ];
  const topicOptions =
    allTopics?.map((topic) => ({
      label: topic.title,
      value: topic.id,
    })) || [];

  const filterTypes = [
    {
      id: 1,
      label: 'Status',
      field: 'status',
      options: [allOption, ...statusOptions],
    },
    {
      id: 2,
      label: 'Topic',
      field: 'topics',
      options: [allOption, ...topicOptions],
    },
    {
      id: 3,
      label: 'Author',
      field: 'author',
      nestedKey: 'recentIncoming.messageType',
      options: [allOption, ...(authors ? convertToSelectOptions(authors) : [])],
    },
  ];

  const handleChangeSearchOptions = (newSearchOptions: SearchQuery) => {
    setSearchQuery({ ...newSearchOptions, pagination: { page: 1, limit: 20 } });
  };

  const handleChangeFilter = (option: SelectOption, field: string) => {
    handleChangeSearchOptions({
      ...searchQuery,
      filters: getUpdatedConvoPromptsFilters({
        filters: searchQuery.filters,
        option,
        field,
      }),
    });
  };

  const handleToggleOpen = (id) => {
    setOpenFilterId(id === openFilterId ? null : id);
  };

  useEffect(() => {
    getAuthorsAndTopics();
  }, []);

  const handleSearch = (text) => {
    setSearchString(text);
  };

  const [searchDebounced] = useDebouncedCallback(() => {
    handleChangeSearchOptions({ ...searchQuery, searchString });
  }, 1000);

  useEffect(() => {
    if (searchString !== null) searchDebounced();
  }, [searchString]);

  return (
    <div className="flex flex-wrap lg:flex-nowrap items-center justify-stretch w-full lg:w-4/5 mr-0 lg:mr-3 my-2 md:gap-x-3">
      {filterTypes.map(({ id, label, field, options }) => {
        const activeFilter = searchQuery.filters?.find((f) => f.field === field);
        const optionsWithoutAll = options.filter(({ value }) => value !== 'all');
        const selectedOptions = !activeFilter
          ? [allOption]
          : optionsWithoutAll.filter((o) => activeFilter?.compareValue1.includes(o.value));

        return (
          <CheckboxSelect
            key={id}
            options={options}
            open={openFilterId === id}
            handleOpen={() => handleToggleOpen(id)}
            onBlur={() => setOpenFilterId(null)}
            selectedOptions={selectedOptions}
            label={label}
            className="mt-5 lg:mt-0 w-full md:w-[calc(33%_-_0.75rem)] lg:w-40"
            onChange={(option) => handleChangeFilter(option, field)}
            dataDurableId={`ConversationPrompts${label}Filter`}
          />
        );
      })}
      <div className="w-full lg:min-w-48 lg:max-w-100 mt-4 lg:mt-0 flex">
        <Input
          placeholder="Search Prompts"
          icon="search"
          iconClassNames="ml-3"
          value={searchString}
          className="h-11 pl-9"
          isClearable
          onChangeText={(t: string) => handleSearch(t)}
        />
        <Checkbox
          checked={showArchived}
          onChange={() => setShowArchived(!showArchived)}
          label="Show Archived"
          wrapClassName="w-56 ml-3"
        />
      </div>
    </div>
  );
};

export default ConversationPromptsFilters;
